import { ChangeEvent, CSSProperties, ReactNode } from 'react';
import { Popup } from 'antd-mobile';
import {
  CheckOutline,
  DeleteOutline,
  LeftOutline,
  RightOutline
} from 'antd-mobile-icons';
import Avatar from '@/components/Avatar';
import CustomerSelector from '@/components/CustomerSelector';
import { Customer } from '@/types';
import './styles.css';

interface RecordPageHeaderProps {
  title: string;
  onBack: () => void;
  onDelete?: () => void;
  deleteLabel?: string;
}

export function RecordPageHeader({
  title,
  onBack,
  onDelete,
  deleteLabel = '删除记录'
}: RecordPageHeaderProps) {
  return (
    <header className="record-form-header">
      <div className="record-form-header__inner">
        <button type="button" className="record-form-header__back" onClick={onBack}>
          <LeftOutline aria-hidden="true" />
          <span>返回</span>
        </button>
        <h1>{title}</h1>
        {onDelete ? (
          <button
            type="button"
            className="record-form-header__delete"
            onClick={onDelete}
            aria-label={deleteLabel}
          >
            <DeleteOutline aria-hidden="true" />
          </button>
        ) : (
          <span aria-hidden="true" />
        )}
      </div>
    </header>
  );
}

interface CustomerPickerPopupProps {
  visible: boolean;
  selectedId?: number;
  onClose: () => void;
  onSelect: (customer: Customer) => void;
}

export function CustomerPickerPopup({
  visible,
  selectedId,
  onClose,
  onSelect
}: CustomerPickerPopupProps) {
  return (
    <Popup
      visible={visible}
      onMaskClick={onClose}
      position="bottom"
      bodyStyle={{
        height: 'min(81.1dvh, calc(100dvh - max(104px, var(--ios-safe-top))))',
        borderRadius: '24px 24px 0 0',
        overflow: 'hidden',
        background: 'var(--color-surface)'
      }}
    >
      <section className="record-customer-picker" aria-label="选择客户">
        <header className="record-customer-picker__header">
          <h2>选择客户</h2>
          <button type="button" onClick={onClose} data-haptic="light">关闭</button>
        </header>
        <div className="record-customer-picker__body">
          <CustomerSelector selectedId={selectedId} onSelect={onSelect} />
        </div>
      </section>
    </Popup>
  );
}

interface RecordCustomerFieldProps {
  customer: Customer | null;
  onClick: () => void;
  variant: 'card' | 'open';
}

export function RecordCustomerField({ customer, onClick, variant }: RecordCustomerFieldProps) {
  return (
    <section className={`record-customer-field record-customer-field--${variant}`}>
      <h2 className="record-form-label">客户</h2>
      <button type="button" className="record-customer-field__button" onClick={onClick} data-haptic="selection">
        {customer ? (
          <>
            <Avatar
              src={customer.avatar}
              name={customer.name}
              size={variant === 'open' ? 62 : 48}
              className="record-customer-field__avatar"
            />
            <span className="record-customer-field__identity">
              <strong>{customer.name}</strong>
              <small>{customer.village || '未填写村庄'}</small>
            </span>
          </>
        ) : (
          <span className="record-customer-field__identity record-customer-field__identity--empty">
            <strong>请选择客户</strong>
            <small>从客户列表中选择</small>
          </span>
        )}
        <span className="record-customer-field__change">
          {customer ? '更换' : '选择'}
          {variant === 'open' && <RightOutline aria-hidden="true" />}
        </span>
      </button>
    </section>
  );
}

export interface RecordChoiceOption {
  id: string;
  label: string;
}

interface RecordChoiceGridProps {
  options: RecordChoiceOption[];
  selectedId: string;
  onSelect: (id: string) => void;
  columns: number;
  tone?: 'red' | 'gold';
  variant?: 'filled' | 'radio';
  size?: 'standard' | 'tall';
  ariaLabel: string;
}

export function RecordChoiceGrid({
  options,
  selectedId,
  onSelect,
  columns,
  tone = 'red',
  variant = 'filled',
  size = 'standard',
  ariaLabel
}: RecordChoiceGridProps) {
  return (
    <div
      className={`record-choice-grid record-choice-grid--${tone} record-choice-grid--${variant} record-choice-grid--${size}`}
      style={{ '--record-choice-columns': columns } as CSSProperties}
      role="radiogroup"
      aria-label={ariaLabel}
    >
      {options.map((option) => {
        const selected = option.id === selectedId;
        return (
          <button
            key={option.id}
            type="button"
            className={selected ? 'is-selected' : ''}
            onClick={() => onSelect(option.id)}
            role="radio"
            aria-checked={selected}
            data-haptic="selection"
          >
            <span>{option.label}</span>
            {variant === 'radio' && (
              <span className="record-choice-grid__radio" aria-hidden="true">
                {selected && <CheckOutline />}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

interface RecordQuantityFieldProps {
  value: string;
  unit: string;
  step: string;
  variant: 'sale';
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

export function RecordQuantityField({
  value,
  unit,
  step,
  variant,
  onChange
}: RecordQuantityFieldProps) {
  return (
    <label className={`record-quantity record-quantity--${variant}`}>
      <span className="record-quantity__sr-label">数量</span>
      <input
        type="number"
        inputMode="decimal"
        min="0"
        step={step}
        value={value}
        onChange={onChange}
        aria-label="数量"
      />
      <span className="record-quantity__unit">{unit}</span>
    </label>
  );
}

interface RecordActionRowProps {
  label: string;
  value: string;
  onClick: () => void;
}

export function RecordActionRow({ label, value, onClick }: RecordActionRowProps) {
  return (
    <button type="button" className="record-meta-row" onClick={onClick} data-haptic="selection">
      <span>{label}</span>
      <span className="record-meta-row__value">
        <strong>{value}</strong>
        <RightOutline aria-hidden="true" />
      </span>
    </button>
  );
}

interface RecordNoteRowProps {
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

export function RecordNoteRow({ value, onChange }: RecordNoteRowProps) {
  return (
    <label className="record-meta-row record-meta-row--note">
      <span>备注</span>
      <span className="record-meta-row__value">
        <input
          value={value}
          onChange={onChange}
          placeholder="可选"
          aria-label="备注"
        />
        <RightOutline aria-hidden="true" />
      </span>
    </label>
  );
}

interface RecordSummaryProps {
  variant: 'sale';
  label: string;
  value: ReactNode;
  detail: ReactNode;
}

export function RecordSummary({ variant, label, value, detail }: RecordSummaryProps) {
  return (
    <section className={`record-summary record-summary--${variant}`} aria-label={`${label}摘要`}>
      <span className="record-summary__label">{label}</span>
      <span className="record-summary__values">
        <strong>{value}</strong>
        <small>{detail}</small>
      </span>
    </section>
  );
}

interface RecordBottomActionsProps {
  saving: boolean;
  onCancel: () => void;
  onSave: () => void;
}

export function RecordBottomActions({ saving, onCancel, onSave }: RecordBottomActionsProps) {
  return (
    <footer className="record-bottom-actions">
      <button type="button" className="record-bottom-actions__cancel" onClick={onCancel} data-haptic="light">
        取消
      </button>
      <button
        type="button"
        className="record-bottom-actions__save"
        onClick={onSave}
        disabled={saving}
        data-haptic="medium"
      >
        {saving ? '保存中' : '保存'}
      </button>
    </footer>
  );
}
